import React, { useCallback, useEffect, useState } from "react";
import api, { brl, dateBR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Ban, ArrowLeftRight, Trash2, Plus, Printer } from "lucide-react";
import { ReceiptDialog, PAYMENT_LABELS } from "@/components/SaleReceipt";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EXCHANGE_PAYMENT_ENTRIES = Object.entries(PAYMENT_LABELS).filter(([k]) => k !== "credit_term");

export default function Sales() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [exchangeSale, setExchangeSale] = useState(null);
  const [exItems, setExItems] = useState([]);
  const [exPay, setExPay] = useState("cash");
  const [addPid, setAddPid] = useState("");
  const [reprint, setReprint] = useState(null);

  const load = useCallback(() => api.get("/sales?limit=200").then((r) => setSales(r.data)), []);
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active !== false)));
  }, [load]);

  const cancel = async (id) => {
    if (!confirm("Cancelar esta venda? O estoque será devolvido e a venda marcada como cancelada.")) return;
    try {
      await api.post(`/sales/${id}/cancel`);
      toast.success("Venda cancelada e estoque revertido");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const openExchange = (s) => {
    setExchangeSale(s);
    setExItems(s.items.map((i) => ({ ...i })));
    setExPay(s.payment_method === "credit_term" ? "cash" : s.payment_method);
    setAddPid("");
  };
  const exAdd = () => {
    const p = products.find((x) => x.id === addPid);
    if (!p) return;
    if (exItems.find((i) => i.product_id === p.id)) return toast.error("Já no carrinho");
    setExItems((it) => [...it, { product_id: p.id, name: p.name, price: p.price, quantity: 1 }]);
    setAddPid("");
  };
  const exUpd = (idx, k, v) => setExItems((it) => it.map((i, j) => j === idx ? { ...i, [k]: v } : i));
  const exDel = (idx) => setExItems((it) => it.filter((_, j) => j !== idx));
  const exTotal = exItems.reduce((s, i) => s + Number(i.quantity) * Number(i.price), 0);

  const doExchange = async () => {
    if (!exItems.length) return toast.error("Informe os itens novos");
    try {
      await api.post(`/sales/${exchangeSale.id}/exchange`, {
        new_items: exItems.map((i) => ({ ...i, quantity: Number(i.quantity), price: Number(i.price) })),
        payment_method: exPay,
        reason: "Troca",
      });
      toast.success("Troca realizada: venda original cancelada e nova venda criada");
      setExchangeSale(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Vendas</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Histórico de Vendas</h1>
      </div>

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead><TableHead>Cliente</TableHead>
              <TableHead>Itens</TableHead><TableHead>Pagamento</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {sales.map((s) => (
                <TableRow key={s.id} data-testid={`sale-row-${s.id}`} className={s.status === "cancelled" ? "opacity-60" : ""}>
                  <TableCell className="text-sm">{dateBR(s.created_at)}</TableCell>
                  <TableCell>{s.customer || "—"}</TableCell>
                  <TableCell><Badge variant="outline">{s.items.length}</Badge></TableCell>
                  <TableCell className="text-sm">{PAYMENT_LABELS[s.payment_method] || s.payment_method}</TableCell>
                  <TableCell className="text-right kpi-num text-emerald-600">{brl(s.total)}</TableCell>
                  <TableCell>
                    {s.status === "cancelled" ? <Badge variant="destructive">Cancelada</Badge> : <Badge className="bg-emerald-600">Concluída</Badge>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="mr-1" onClick={() => setReprint(s)} data-testid={`reprint-sale-${s.id}`}>
                      <Printer className="w-3.5 h-3.5 mr-1" /> Reimprimir
                    </Button>
                    {user?.role === "admin" && s.status !== "cancelled" && (
                      <>
                        <Button size="sm" variant="outline" className="mr-1" onClick={() => openExchange(s)} data-testid={`exchange-sale-${s.id}`}>
                          <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Trocar
                        </Button>
                        <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => cancel(s.id)} data-testid={`cancel-sale-${s.id}`}>
                          <Ban className="w-3.5 h-3.5 mr-1" /> Cancelar
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {sales.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nenhuma venda registrada.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ReceiptDialog sale={reprint} onClose={() => setReprint(null)} title="Reimpressão de Comprovante" />

      <Dialog open={!!exchangeSale} onOpenChange={(v) => !v && setExchangeSale(null)}>
        <DialogContent className="max-w-2xl" data-testid="exchange-dialog">
          <DialogHeader><DialogTitle className="font-display">Troca de produtos</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              Venda original será <b>cancelada</b> e uma nova venda será criada com os itens abaixo. O estoque é ajustado automaticamente.
            </div>
            <div>
              <Label>Adicionar item</Label>
              <div className="flex gap-2">
                <Select value={addPid} onValueChange={setAddPid}>
                  <SelectTrigger className="flex-1" data-testid="ex-add-product"><SelectValue placeholder="Selecione produto" /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {brl(p.price)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" onClick={exAdd} disabled={!addPid}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            {exItems.length > 0 && (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="w-20 text-right">Qtd</TableHead>
                    <TableHead className="w-28 text-right">Preço</TableHead>
                    <TableHead className="w-24 text-right">Total</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {exItems.map((it, idx) => (
                      <TableRow key={`${it.product_id}-${idx}`}>
                        <TableCell className="text-sm">{it.name}</TableCell>
                        <TableCell><Input type="number" value={it.quantity} onChange={(e) => exUpd(idx, "quantity", e.target.value)} className="h-8 text-right" /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={it.price} onChange={(e) => exUpd(idx, "price", e.target.value)} className="h-8 text-right" /></TableCell>
                        <TableCell className="text-right kpi-num">{brl(Number(it.quantity) * Number(it.price))}</TableCell>
                        <TableCell><Button size="icon" variant="ghost" className="text-rose-600 h-7 w-7" onClick={() => exDel(idx)}><Trash2 className="w-3 h-3" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Label>Pagamento</Label>
                <Select value={exPay} onValueChange={setExPay}>
                  <SelectTrigger data-testid="ex-pay"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXCHANGE_PAYMENT_ENTRIES.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Novo total</div>
                <div className="kpi-num text-2xl text-blue-600">{brl(exTotal)}</div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExchangeSale(null)}>Cancelar</Button>
            <Button onClick={doExchange} className="bg-blue-600 hover:bg-blue-700" data-testid="do-exchange-btn">Confirmar Troca</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
